import { useEffect, useState } from "react";
import { Download, Loader2, CalendarDays, Check } from "lucide-react";
import { toast } from "sonner";
import { getFestivals, updateFestival, festivalDownloadUrl } from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export function FestivalsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () =>
    getFestivals()
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => toast.error("Impossibile caricare le feste"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const setActive = async (slug, active) => {
    setBusy(slug);
    try {
      await updateFestival(slug, { active });
      await load();
      toast.success(active ? "Tema attivato sul sito" : "Tema disattivato");
    } catch {
      toast.error("Operazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const toggleAlerts = async (slug, alerts_enabled) => {
    try {
      await updateFestival(slug, { alerts_enabled });
      setItems((prev) => prev.map((f) => (f.slug === slug ? { ...f, alerts_enabled } : f)));
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  return (
    <div className="p-6 lg:p-12 max-w-6xl" data-testid="festivals-view">
      <div className="mb-8">
        <h2 className="font-serif-display text-3xl text-[#E8E2D2]">Feste ed Eventi</h2>
        <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mt-1">
          Attiva un tema stagionale sul sito · scarica la grafica social · ricevi un promemoria 14 e 7 giorni prima
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#E8E2D2]/40 font-mono-accent text-xs uppercase tracking-widest mt-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => (
            <div
              key={f.slug}
              className={`border bg-[#141A14] flex flex-col ${f.active ? "border-[#D47738]" : "border-white/15"}`}
              data-testid={`festival-card-${f.slug}`}
              style={f.active ? { borderColor: f.accent } : undefined}
            >
              <div className="relative aspect-square bg-[#0D110D] overflow-hidden">
                <img src={f.image_url} alt={`Birraio a tema ${f.name}`} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-3 left-3 w-4 h-4 rounded-full border border-white/40" style={{ backgroundColor: f.accent }} />
                {f.active && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 bg-[#D47738] text-[#0D110D] font-mono-accent text-[9px] uppercase tracking-[0.15em] px-2 py-1 font-semibold" style={{ backgroundColor: f.accent }} data-testid={`festival-active-badge-${f.slug}`}>
                    <Check className="w-3 h-3" /> Attivo
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col gap-3 flex-1">
                <div>
                  <p className="font-serif-display text-xl text-[#E8E2D2] leading-tight">{f.name}</p>
                  <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#E8E2D2]/40 mt-1 flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" /> {fmtDate(f.date)} · tra {f.days_until}g
                  </p>
                </div>

                <button
                  onClick={() => setActive(f.slug, !f.active)}
                  disabled={busy === f.slug}
                  className={`inline-flex items-center justify-center gap-2 py-2.5 font-mono-accent text-[10px] uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${f.active ? "border border-white/20 text-[#E8E2D2] hover:border-[#9E3C3C] hover:text-[#9E3C3C]" : "bg-[#D47738] text-[#0D110D] hover:bg-[#E8E2D2]"}`}
                  data-testid={`festival-toggle-${f.slug}`}
                >
                  {busy === f.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {f.active ? "Disattiva tema" : "Attiva tema"}
                </button>

                <a
                  href={festivalDownloadUrl(f.slug)}
                  className="inline-flex items-center justify-center gap-2 border border-white/20 py-2.5 font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#E8E2D2] hover:bg-[#D47738] hover:text-[#0D110D] hover:border-[#D47738] transition-colors"
                  data-testid={`festival-download-${f.slug}`}
                >
                  <Download className="w-3.5 h-3.5" /> Scarica grafica
                </a>

                <label className="flex items-center gap-2 cursor-pointer mt-1" data-testid={`festival-alerts-label-${f.slug}`}>
                  <input
                    type="checkbox"
                    checked={!!f.alerts_enabled}
                    onChange={(e) => toggleAlerts(f.slug, e.target.checked)}
                    className="accent-[#D47738] w-4 h-4"
                    data-testid={`festival-alerts-${f.slug}`}
                  />
                  <span className="font-mono-accent text-[9px] uppercase tracking-widest text-[#E8E2D2]/60">
                    Promemoria email (14 + 7 giorni)
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
